import { Input, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { BodyOf, ResponseOf } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import Select from "../../../../../shared/components/Select";
import useApi from "../../../../../shared/hooks/useApi";
import type {
  CaregiverId,
  InstaPayBankAccountTypeId,
  InstaPayBankId,
} from "../../../../../shared/schema/schema";
import EntityProfileActionBar from "../../../../entityProfileModal/components/EntityProfileActionBar";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import {
  EntityProfileControlFieldRow,
  EntityProfileFieldSection,
} from "../../../../entityProfileModal/components/EntityProfileFieldLayout";

type BankInformationResponse = ResponseOf<"get", "./insta_pay_info/bank_information">;
type BankInformation = NonNullable<BankInformationResponse["bankAccountInformation"]>;
type BankInformationBody = BodyOf<"post", "./insta_pay_info/bank_information">;

type Props = {
  caregiverId: CaregiverId;
};

type DirectDepositForm = {
  accountNumber: string;
  bankAccountTypeId: InstaPayBankAccountTypeId | null;
  bankId: InstaPayBankId | null;
  hasAgreedToTerms: boolean;
  routingNumber: string;
};

const emptyForm: DirectDepositForm = {
  accountNumber: "",
  bankAccountTypeId: null,
  bankId: null,
  hasAgreedToTerms: false,
  routingNumber: "",
};

export default function CaregiverProfileDirectDepositSection(props: Props) {
  const { api } = useApi();
  const canEdit = auth.isAllowedTo("edit_caregiver_direct_deposit_information");
  const toast = useToast({ position: "top-right" });
  const queryClient = useQueryClient();
  const [isEditing, setIsInEditMode] = React.useState(false);
  const isInEditMode = canEdit && isEditing;
  const queryKey = ["caregiverProfileModal", props.caregiverId, "directDeposit"];
  const banksQuery = useQuery({
    queryKey: ["caregiverProfileModal", "directDeposit", "banks"],
    queryFn: () => api.get("./insta_pay_info/banks", {}),
  });
  const accountTypesQuery = useQuery({
    queryKey: ["caregiverProfileModal", "directDeposit", "bankAccountTypes"],
    queryFn: () => api.get("./insta_pay_info/bank_account_types", {}),
  });
  const bankInformationQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get("./insta_pay_info/bank_information", {
        query: { caregiverId: props.caregiverId },
      }),
  });
  const [form, setForm] = React.useState<DirectDepositForm>(emptyForm);
  const bankInformation = bankInformationQuery.data?.bankAccountInformation ?? null;
  const isLoading =
    banksQuery.isLoading || accountTypesQuery.isLoading || bankInformationQuery.isLoading;

  React.useEffect(() => {
    if (bankInformation === null) {
      setForm(emptyForm);
      return;
    }

    setForm(formatBankInformationForm(bankInformation));
  }, [bankInformation]);

  const updateMutation = useMutation({
    mutationFn: (body: BankInformationBody) =>
      api.post("./insta_pay_info/bank_information", {
        body,
        query: { caregiverId: props.caregiverId },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setIsInEditMode(false);
      toast({ status: "success", title: "Successfully updated bank account information" });
    },
    onError: (error) => {
      toast({
        status: "error",
        title: "Couldn't save bank account information",
        description: String(error),
      });
    },
  });
  const bankOptions = React.useMemo(
    () =>
      (banksQuery.data?.banks ?? []).map((bank) => ({
        label: bank.name,
        value: bank.id,
      })),
    [banksQuery.data?.banks]
  );
  const accountTypeOptions = React.useMemo(
    () =>
      (accountTypesQuery.data?.accountTypes ?? []).map((accountType) => ({
        label: accountType.type,
        value: accountType.id,
      })),
    [accountTypesQuery.data?.accountTypes]
  );
  const selectedBankName = bankOptions.find((bank) => bank.value === form.bankId)?.label ?? "";
  const selectedAccountTypeName =
    accountTypeOptions.find((accountType) => accountType.value === form.bankAccountTypeId)?.label ??
    "";

  const handleDone = () => {
    if (!canEdit) {
      return;
    }
    if (
      form.accountNumber === "" ||
      form.routingNumber === "" ||
      form.bankAccountTypeId === null ||
      form.bankId === null
    ) {
      toast({ status: "error", title: "All bank account fields must be filled" });
      return;
    }

    updateMutation.mutate({
      accountNumber: form.accountNumber,
      bankAccountTypeId: form.bankAccountTypeId,
      bankId: form.bankId,
      hasAgreedToTerms: form.hasAgreedToTerms,
      routingNumber: form.routingNumber,
    });
  };

  if (isLoading) {
    return (
      <Text color="gray.600" fontSize="14px">
        Loading payroll...
      </Text>
    );
  }

  return (
    <VStack alignItems="stretch" spacing={4}>
      {canEdit ? (
        <EntityProfileActionBar>
          {isInEditMode ? (
            <EntityModalButton
              isLoading={updateMutation.isPending}
              kind="blue"
              size="sm"
              onClick={handleDone}
            >
              Done
            </EntityModalButton>
          ) : (
            <EntityModalButton
              iconClassName="fa fa-pencil"
              kind="blue"
              size="sm"
              onClick={() => setIsInEditMode(true)}
            >
              Edit
            </EntityModalButton>
          )}
        </EntityProfileActionBar>
      ) : null}
      <EntityProfileFieldSection maxW="760px">
        <DirectDepositField label="Bank Account Type">
          {isInEditMode ? (
            <Select
              buttonProps={{ w: "260px", bg: "white" }}
              label="Select"
              multiple={false}
              options={accountTypeOptions}
              value={form.bankAccountTypeId}
              onChange={(value) =>
                setForm((current) => ({ ...current, bankAccountTypeId: value ?? null }))
              }
            />
          ) : (
            selectedAccountTypeName
          )}
        </DirectDepositField>
        <DirectDepositField label="Bank">
          {isInEditMode ? (
            <Select
              buttonProps={{ w: "260px", bg: "white" }}
              label="Select"
              multiple={false}
              options={bankOptions}
              searchable={true}
              value={form.bankId}
              onChange={(value) => setForm((current) => ({ ...current, bankId: value ?? null }))}
            />
          ) : (
            selectedBankName
          )}
        </DirectDepositField>
        <DirectDepositField label="Routing Number">
          {isInEditMode ? (
            <Input
              maxW="260px"
              value={form.routingNumber}
              onChange={(event) =>
                setForm((current) => ({ ...current, routingNumber: event.target.value }))
              }
            />
          ) : (
            form.routingNumber
          )}
        </DirectDepositField>
        <DirectDepositField label="Account Number">
          {isInEditMode ? (
            <Input
              maxW="260px"
              value={form.accountNumber}
              onChange={(event) =>
                setForm((current) => ({ ...current, accountNumber: event.target.value }))
              }
            />
          ) : (
            form.accountNumber
          )}
        </DirectDepositField>
      </EntityProfileFieldSection>
      {updateMutation.isPending ? <Spinner alignSelf="flex-start" size="sm" /> : null}
    </VStack>
  );
}

function DirectDepositField(props: { children: React.ReactNode; label: string }) {
  return (
    <EntityProfileControlFieldRow label={props.label}>
      {props.children}
    </EntityProfileControlFieldRow>
  );
}

function formatBankInformationForm(bankInformation: BankInformation): DirectDepositForm {
  return {
    accountNumber: bankInformation.accountNumber ?? "",
    bankAccountTypeId: bankInformation.bankAccountTypeId,
    bankId: bankInformation.bankId,
    hasAgreedToTerms: bankInformation.hasAgreedToTerms,
    routingNumber: bankInformation.routingNumber ?? "",
  };
}
